
import React, { useState } from 'react';
import { ImageEdits, getSafeImageEdits } from '../types';
import { uploadFile } from '../storageUtils';

interface ImageInputProps {
    label: string;
    imageUrl: string | null;
    onUploadComplete: (url: string) => void;
    onLaunchEditor: (url?: string) => void;
    imageEdits: ImageEdits;
    previewShape: 'circle' | 'square';
    storagePath: string;
}

const ImageInput: React.FC<ImageInputProps> = ({ label, imageUrl, onUploadComplete, onLaunchEditor, imageEdits, previewShape, storagePath }) => {
    const [isUploading, setIsUploading] = useState(false);
    
    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setIsUploading(true);
            try {
                const downloadURL = await uploadFile(file, storagePath);
                onUploadComplete(downloadURL);
                // Automatically launch editor with the new URL
                onLaunchEditor(downloadURL);
            } catch (error: any) {
                console.error("Upload failed:", error);
                alert(`Upload failed: ${error.message || 'Unknown error'}`);
            } finally {
                setIsUploading(false);
            }
        }
    };
    
    const safeEdits = getSafeImageEdits(imageEdits);
    const imageStyle: React.CSSProperties = {
        filter: `brightness(${safeEdits.adjustments.brightness}%) contrast(${safeEdits.adjustments.contrast}%) saturate(${safeEdits.adjustments.saturate}%) blur(${safeEdits.adjustments.blur}px)`,
        transform: `scale(${safeEdits.adjustments.zoom}) translate(${safeEdits.adjustments.offsetX}px, ${safeEdits.adjustments.offsetY}px)`,
    };

    const shapeClass = previewShape === 'circle' ? 'rounded-full' : 'rounded-lg';

    return (
        <div>
            <label className="block text-sm font-medium text-gray-300">{label}</label>
            <div className="mt-2 flex items-center space-x-4">
                <div className={`relative w-24 h-24 bg-white/10 border border-white/20 overflow-hidden ${shapeClass}`}>
                    {imageUrl ? (
                        <div className="filter-wrapper w-full h-full">
                            <div className={`w-full h-full ${safeEdits.filter}`}>
                                <img src={imageUrl} alt="Preview" className="w-full h-full object-contain" style={imageStyle} />
                            </div>
                        </div>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/50">
                            <i className="ph-duotone ph-image text-4xl"></i>
                        </div>
                    )}
                    {isUploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <div className="spinner-white"></div>
                        </div>
                    )}
                </div>
                <div className="space-y-2">
                     <label htmlFor={`${label.replace(' ', '-')}-file-input`} className="cursor-pointer bg-transparent py-2 px-3 border border-white/50 rounded-md shadow-sm text-sm leading-4 font-medium text-white hover:bg-white/10 block text-center transition-colors">
                        Upload
                    </label>
                    <input id={`${label.replace(' ', '-')}-file-input`} type="file" className="sr-only" onChange={handleFileChange} accept="image/*" disabled={isUploading}/>
                    <button type="button" onClick={() => onLaunchEditor()} disabled={!imageUrl || isUploading} className="w-full bg-transparent py-2 px-3 border border-white/50 rounded-md shadow-sm text-sm leading-4 font-medium text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        Edit
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageInput;
